import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Plus, Layers, Pencil, Trash2 } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Panel, EmptyState } from "@/components/admin/ui";
import { centsToDisplay, dollarsInputToCents } from "@/lib/money";
import { usePolling } from "@/hooks/usePolling";
import { LastUpdated } from "@/components/common/LastUpdated";
import { AddingProgressBar } from "@/components/common/AddingProgressBar";
import { BundleFormFields } from "@/components/org/bundles/BundleFormFields";
import { BundleEditModal } from "@/components/org/bundles/BundleEditModal";

const EMPTY_FORM = { name: "", sku: "", description: "", service_ids: [], product_ids: [], duration_minutes: "", durationTouched: false, price: "" };

export default function OrgBundles() {
  const { API, user } = useAuth();
  const isManager = user?.role === "org_owner" || user?.role === "org_admin";
  const [bundles, setBundles] = useState(null);
  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [saving, setSaving] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [editingBundle, setEditingBundle] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const load = useCallback(async (silent) => {
    try {
      const [bundlesRes, servicesRes, productsRes] = await Promise.all([
        axios.get(`${API}/org/bundles`, { withCredentials: true }),
        axios.get(`${API}/org/services`, { withCredentials: true }),
        axios.get(`${API}/org/products`, { withCredentials: true }),
      ]);
      setBundles(bundlesRes.data.bundles);
      setServices(servicesRes.data.services);
      setProducts(productsRes.data.products);
      setLastUpdated(Date.now());
    } catch (err) {
      if (!silent) setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, [API]);

  useEffect(() => { load(); }, [load]);
  usePolling(() => {
    if (editingBundle || saving || savingId) return;
    load(true);
  }, 15000);

  const activeServices = services.filter((s) => s.active);
  const activeProducts = products.filter((p) => p.active);
  const visibleBundles = bundles || [];

  const addBundle = async () => {
    if (!form.name.trim() || form.price === "") {
      setError("Bundle name and price are required.");
      return;
    }
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const payload = {
        name: form.name.trim(),
        sku: form.sku.trim() || null,
        description: form.description.trim() || null,
        service_ids: form.service_ids,
        product_ids: form.product_ids,
        price_cents: dollarsInputToCents(form.price),
        duration_minutes: form.duration_minutes === "" ? null : parseInt(form.duration_minutes, 10),
        active: true,
      };
      await axios.post(`${API}/org/bundles`, payload, { withCredentials: true });
      setForm(EMPTY_FORM);
      setSuccess("Bundle added.");
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't add this bundle.");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (bundle) => {
    setSavingId(bundle.bundle_id);
    try {
      await axios.patch(`${API}/org/bundles/${bundle.bundle_id}`, { active: !bundle.active }, { withCredentials: true });
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingId(null);
    }
  };

  const removeBundle = async (bundle) => {
    setSavingId(bundle.bundle_id);
    setError("");
    try {
      await axios.delete(`${API}/org/bundles/${bundle.bundle_id}`, { withCredentials: true });
      setConfirmDeleteId(null);
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingId(null);
    }
  };

  const bundleItemSummary = (b) => {
    const svcCount = (b.service_ids || []).length;
    const prodCount = (b.product_ids || []).length;
    const parts = [];
    if (svcCount) parts.push(`${svcCount} service${svcCount === 1 ? "" : "s"}`);
    if (prodCount) parts.push(`${prodCount} product${prodCount === 1 ? "" : "s"}`);
    return parts.join(" · ") || "No items yet";
  };

  if (bundles === null && !error) {
    return <div className="rounded-xl border border-slate-200 bg-slate-50 p-10 text-center text-sm font-semibold text-slate-500 dark:border-white/10 dark:bg-white/[0.03] dark:text-slate-400" data-testid="org-bundles-loading">Loading bundles…</div>;
  }

  return (
    <div className="space-y-6" data-testid="org-bundles-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-950 dark:text-white">Bundles</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Combine your Services and Products into reusable, discounted packages.</p>
        </div>
        <LastUpdated timestamp={lastUpdated} testId="org-bundles-last-updated" />
      </div>

      {error && <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="org-bundles-error">{error}</p>}

      <div className={isManager ? "grid grid-cols-1 gap-6 lg:grid-cols-[3fr_2fr] lg:items-start" : ""}>
        <Panel title="Your Bundles">
          {visibleBundles.length === 0 ? (
            <EmptyState icon={Layers} title="No bundles yet." message={isManager ? "Create your first bundle to get started." : "Ask your organization owner or admin to create one."} testId="org-bundles-empty" />
          ) : (
            <div className="space-y-2" data-testid="org-bundles-list">
              {visibleBundles.map((b) => (
                <div key={b.bundle_id} className="flex flex-wrap items-center gap-4 rounded-xl border border-slate-200 p-4 dark:border-white/10" data-testid={`org-bundle-row-${b.bundle_id}`}>
                  <div className="min-w-[180px] flex-1">
                    <p className="text-sm font-bold text-slate-950 dark:text-white">{b.name}{b.sku ? <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-500 dark:bg-white/10 dark:text-slate-400">{b.sku}</span> : null}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {centsToDisplay(b.price_cents)}{b.duration_minutes ? ` · ${b.duration_minutes} min` : ""} · {bundleItemSummary(b)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {isManager && (
                      <button
                        type="button"
                        onClick={() => setEditingBundle(b)}
                        data-testid={`org-bundle-edit-btn-${b.bundle_id}`}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-50 dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/10"
                      >
                        <Pencil size={12} /> Edit
                      </button>
                    )}
                    {isManager ? (
                      <button
                        type="button"
                        onClick={() => toggleActive(b)}
                        disabled={savingId === b.bundle_id}
                        data-testid={`org-bundle-toggle-${b.bundle_id}`}
                        className={`rounded-lg border px-3 py-1.5 text-xs font-bold ${b.active ? "border-emerald-200 text-emerald-600 hover:bg-emerald-50 dark:border-emerald-500/30 dark:text-emerald-400 dark:hover:bg-emerald-500/10" : "border-slate-200 text-slate-500 hover:bg-slate-50 dark:border-white/15 dark:text-slate-400 dark:hover:bg-white/10"}`}
                      >
                        {b.active ? "Active" : "Inactive"}
                      </button>
                    ) : (
                      <span data-testid={`org-bundle-status-${b.bundle_id}`} className={`rounded-lg border px-3 py-1.5 text-xs font-bold ${b.active ? "border-emerald-200 text-emerald-600 dark:border-emerald-500/30 dark:text-emerald-400" : "border-slate-200 text-slate-500 dark:border-white/15 dark:text-slate-400"}`}>
                        {b.active ? "Active" : "Inactive"}
                      </span>
                    )}
                    {isManager && (
                      confirmDeleteId === b.bundle_id ? (
                        <div className="flex items-center gap-1.5" data-testid={`org-bundle-confirm-delete-${b.bundle_id}`}>
                          <button type="button" onClick={() => removeBundle(b)} disabled={savingId === b.bundle_id} data-testid={`org-bundle-confirm-delete-yes-${b.bundle_id}`} className="rounded-lg bg-red-600 px-2.5 py-1.5 text-xs font-bold text-white hover:bg-red-700 disabled:opacity-50">Delete?</button>
                          <button type="button" onClick={() => setConfirmDeleteId(null)} data-testid={`org-bundle-confirm-delete-cancel-${b.bundle_id}`} className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-bold text-slate-500 dark:border-white/15 dark:text-slate-400">Cancel</button>
                        </div>
                      ) : (
                        <button type="button" onClick={() => setConfirmDeleteId(b.bundle_id)} disabled={savingId === b.bundle_id} data-testid={`org-bundle-delete-${b.bundle_id}`} className="rounded-lg border border-red-200 p-1.5 text-red-600 hover:bg-red-50 disabled:opacity-50 dark:border-red-500/30 dark:text-red-400 dark:hover:bg-red-500/10">
                          <Trash2 size={14} />
                        </button>
                      )
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>

        {isManager && (
          <Panel title="Add a Bundle">
            <BundleFormFields services={activeServices} products={activeProducts} form={form} setForm={setForm} testIdPrefix="org-bundle-create" />
            {success && <p className="mt-4 rounded-xl bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400" data-testid="org-bundles-success">{success}</p>}
            <button type="button" onClick={addBundle} disabled={saving || !form.name.trim() || form.price === ""} data-testid="org-bundle-add-btn" className="mt-4 inline-flex items-center gap-2 rounded-xl bg-[#0052FF] px-5 py-2.5 text-sm font-bold text-white disabled:opacity-50">
              <Plus size={16} /> {saving ? "Adding…" : "Add Bundle"}
            </button>
            <AddingProgressBar active={saving} label="Adding bundle…" testId="org-bundle-adding-progress" />
          </Panel>
        )}
      </div>

      {editingBundle && (
        <BundleEditModal
          bundle={editingBundle}
          services={activeServices}
          products={activeProducts}
          onClose={() => setEditingBundle(null)}
          onSaved={() => load()}
        />
      )}
    </div>
  );
}
